import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  FileText,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  Filter,
  Calendar,
  Globe,
  Lock,
  Zap,
  BarChart3,
  TrendingUp,
  AlertCircle,
  Info
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface ComplianceSummary {
  report_period: { start: string; end: string };
  compliance_status: string;
  total_security_events: number;
  cross_tenant_access_attempts: number;
  cross_tenant_target: number;
  rate_limit_violations: number;
  data_leakage_preventions: number;
  critical_events: number;
  high_risk_events: number;
  active_tenants: number;
  tenants_with_events: number;
  unique_blocked_users: number;
  unique_blocked_ips: number;
}

interface ComplianceCheck {
  control: string;
  status: string;
  description: string;
  events_count: number | null;
  target: number | null;
  risk_level: string;
}

interface TenantBreakdown {
  tenant_id: number | null;
  tenant_name: string;
  cross_tenant_blocks: number;
  rate_limit_violations: number;
  data_leakage_preventions: number;
  total_events: number;
  critical_events: number;
  high_events: number;
  unique_ips: number;
  unique_users: number;
  first_event: string;
  last_event: string;
}

interface SecurityEvent {
  id: number;
  event_type: string;
  event_timestamp: string;
  user_id: number;
  user_name: string;
  ip_address: string;
  user_agent: string;
  status: string;
  risk_level: string;
  tenant_id: number;
  tenant_name: string;
  table_name: string;
  resource_name: string;
  new_values: Record<string, unknown>;
}

interface StoredReport {
  id: number;
  period: string;
  compliance_status: string;
  total_security_events: number;
  cross_tenant_access_attempts: number;
  rate_limit_violations: number;
  data_leakage_preventions: number;
  critical_events: number;
  generated_at: string;
  report_data: Record<string, unknown>;
}

interface ComplianceReport {
  summary: ComplianceSummary;
  compliance_checklist: ComplianceCheck[];
  tenant_breakdown: TenantBreakdown[];
  recent_critical_events: SecurityEvent[];
  event_counts: {
    cross_tenant_blocks: number;
    rate_limit_violations: number;
    data_leakage_preventions: number;
  };
  framework_references: string[];
  generated_at: string;
}

const TenantIsolationCompliance: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'live-report' | 'security-events' | 'stored-reports'>('live-report');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [storedReports, setStoredReports] = useState<StoredReport[]>([]);
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null);
  const [expandedReport, setExpandedReport] = useState<number | null>(null);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [riskLevelFilter, setRiskLevelFilter] = useState('');

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/security/tenant-isolation-report', {
        params: { start_date: startDate, end_date: endDate }
      });
      if (response.data.success) {
        setReport(response.data);
      }
    } catch (error: unknown) {
      console.error('Error fetching compliance report:', error);
      toast.error('Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page: eventsPage,
        limit: 25,
        start_date: startDate,
        end_date: endDate
      };
      if (eventTypeFilter) params.event_type = eventTypeFilter;
      if (riskLevelFilter) params.risk_level = riskLevelFilter;

      const response = await api.get('/api/admin/security/security-events', { params });
      if (response.data.success) {
        setEvents(response.data.events);
        setEventsTotalPages(response.data.pagination.pages);
        setEventsTotal(response.data.pagination.total);
      }
    } catch (error: unknown) {
      console.error('Error fetching security events:', error);
      toast.error('Failed to load security events');
    } finally {
      setLoading(false);
    }
  }, [eventsPage, startDate, endDate, eventTypeFilter, riskLevelFilter]);

  const fetchStoredReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/security/compliance-reports');
      if (response.data.success) {
        setStoredReports(response.data.reports);
      }
    } catch (error: unknown) {
      console.error('Error fetching stored reports:', error);
      toast.error('Failed to load stored reports');
    } finally {
      setLoading(false);
    }
  }, []);

  const generateReportNow = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/admin/security/generate-compliance-report');
      if (response.data.success) {
        toast.success('Compliance report generated successfully');
        fetchStoredReports();
      }
    } catch (error: unknown) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate compliance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'live-report') fetchReport();
    else if (activeTab === 'security-events') fetchEvents();
    else if (activeTab === 'stored-reports') fetchStoredReports();
  }, [activeTab, fetchReport, fetchEvents, fetchStoredReports]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLIANT':
      case 'PASS':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'NEEDS_REVIEW':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'NON_COMPLIANT':
      case 'FAIL':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLIANT':
      case 'PASS':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'NEEDS_REVIEW':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'NON_COMPLIANT':
      case 'FAIL':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getRiskBadge = (level: string) => {
    const colors: Record<string, string> = {
      CRITICAL: 'bg-red-100 text-red-700 border-red-300',
      HIGH: 'bg-orange-100 text-orange-700 border-orange-300',
      MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      LOW: 'bg-green-100 text-green-700 border-green-300'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colors[level] || 'bg-gray-100 text-gray-700'}`}>
        {level}
      </span>
    );
  };

  const getEventTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cross_tenant_access_blocked: 'Cross-Tenant Block',
      rate_limit_exceeded: 'Rate Limit Exceeded',
      data_leakage_prevented: 'Data Leakage Prevented'
    };
    return labels[type] || type;
  };

  const getEventTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      cross_tenant_access_blocked: 'bg-red-100 text-red-700',
      rate_limit_exceeded: 'bg-orange-100 text-orange-700',
      data_leakage_prevented: 'bg-purple-100 text-purple-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
        {getEventTypeLabel(type)}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderDateFilters = () => (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center space-x-2">
        <Calendar className="w-4 h-4 text-gray-400" />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <span className="text-gray-400">to</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <button
        onClick={() => {
          if (activeTab === 'live-report') fetchReport();
          else fetchEvents();
        }}
        disabled={loading}
        className="flex items-center space-x-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50"
      >
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        <span>Refresh</span>
      </button>
    </div>
  );

  const renderLiveReport = () => {
    if (!report) return null;
    const { summary, compliance_checklist, tenant_breakdown, recent_critical_events, framework_references } = report;

    return (
      <div className="space-y-6">
        {renderDateFilters()}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`rounded-xl border-2 p-5 ${getStatusColor(summary.compliance_status)}`}>
            <div className="flex items-center justify-between mb-2">
              {getStatusIcon(summary.compliance_status)}
              <Shield className="w-8 h-8 opacity-20" />
            </div>
            <div className="text-2xl font-bold">{summary.compliance_status}</div>
            <div className="text-sm opacity-75">Overall Status</div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Lock className="w-5 h-5 text-red-500" />
              <span className="text-3xl font-bold text-red-600">{summary.cross_tenant_access_attempts}</span>
            </div>
            <div className="text-sm text-gray-500">Cross-Tenant Blocks</div>
            <div className="text-xs text-gray-400 mt-1">Target: {summary.cross_tenant_target}</div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-5 h-5 text-orange-500" />
              <span className="text-3xl font-bold text-orange-600">{summary.rate_limit_violations}</span>
            </div>
            <div className="text-sm text-gray-500">Rate Limit Violations</div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <ShieldAlert className="w-5 h-5 text-purple-500" />
              <span className="text-3xl font-bold text-purple-600">{summary.data_leakage_preventions}</span>
            </div>
            <div className="text-sm text-gray-500">Leakage Prevented</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-800">{summary.total_security_events}</div>
            <div className="text-xs text-gray-500">Total Events</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-800">{summary.active_tenants}</div>
            <div className="text-xs text-gray-500">Active Tenants</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{summary.critical_events}</div>
            <div className="text-xs text-gray-500">Critical Events</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-800">{summary.unique_blocked_users}</div>
            <div className="text-xs text-gray-500">Unique Blocked Users</div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-blue-500" />
              <span>Compliance Checklist</span>
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {compliance_checklist.map((check, index) => (
              <div key={index} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(check.status)}
                  <div>
                    <div className="font-medium text-gray-900">{check.control}</div>
                    <div className="text-sm text-gray-500">{check.description}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getRiskBadge(check.risk_level)}
                  {check.events_count !== null && (
                    <span className="text-sm text-gray-500">
                      {check.events_count} event{check.events_count !== 1 ? 's' : ''}
                      {check.target !== null && <span className="text-gray-400"> (target: {check.target})</span>}
                    </span>
                  )}
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    check.status === 'PASS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {check.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {tenant_breakdown.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Globe className="w-5 h-5 text-blue-500" />
                <span>Per-Tenant Breakdown</span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cross-Tenant</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rate Limit</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Leakage</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Critical</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">IPs</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Event</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tenant_breakdown.map((tenant) => {
                    const key = String(tenant.tenant_id || 'system');
                    return (
                      <React.Fragment key={key}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{tenant.tenant_name}</div>
                            {tenant.tenant_id && <div className="text-xs text-gray-400">ID: {tenant.tenant_id}</div>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-medium ${tenant.cross_tenant_blocks > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {tenant.cross_tenant_blocks}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-medium ${tenant.rate_limit_violations > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                              {tenant.rate_limit_violations}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-medium ${tenant.data_leakage_preventions > 0 ? 'text-purple-600' : 'text-green-600'}`}>
                              {tenant.data_leakage_preventions}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center font-medium">{tenant.total_events}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-medium ${tenant.critical_events > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {tenant.critical_events}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">{tenant.unique_ips}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(tenant.last_event)}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => setExpandedTenant(expandedTenant === key ? null : key)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              {expandedTenant === key ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                        {expandedTenant === key && (
                          <tr>
                            <td colSpan={9} className="px-4 py-3 bg-gray-50">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Unique Users:</span>{' '}
                                  <span className="font-medium">{tenant.unique_users}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">High Risk Events:</span>{' '}
                                  <span className="font-medium text-orange-600">{tenant.high_events}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">First Event:</span>{' '}
                                  <span className="font-medium">{formatDate(tenant.first_event)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Last Event:</span>{' '}
                                  <span className="font-medium">{formatDate(tenant.last_event)}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {recent_critical_events.length > 0 && (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm">
            <div className="p-4 border-b border-red-100 bg-red-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-red-800 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span>Recent Critical Events</span>
                <span className="text-sm font-normal text-red-600">({recent_critical_events.length})</span>
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {recent_critical_events.map((event) => (
                <div key={event.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getEventTypeBadge(event.event_type)}
                      <span className="text-sm text-gray-900">{event.user_name || `User #${event.user_id}`}</span>
                      {event.tenant_name && (
                        <span className="text-sm text-gray-500">on {event.tenant_name}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-400">{event.ip_address}</span>
                      <span className="text-sm text-gray-500">{formatDate(event.event_timestamp)}</span>
                    </div>
                  </div>
                  {event.new_values && (
                    <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded p-2 font-mono">
                      {JSON.stringify(event.new_values, null, 2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {framework_references.length > 0 && (
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Framework References</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {framework_references.map((ref, i) => (
                <div key={i} className="text-sm text-blue-700 flex items-center space-x-2">
                  <CheckCircle className="w-3 h-3 text-blue-400 flex-shrink-0" />
                  <span>{ref}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSecurityEvents = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {renderDateFilters()}
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={eventTypeFilter}
            onChange={(e) => { setEventTypeFilter(e.target.value); setEventsPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Event Types</option>
            <option value="cross_tenant_access_blocked">Cross-Tenant Blocks</option>
            <option value="rate_limit_exceeded">Rate Limit Exceeded</option>
            <option value="data_leakage_prevented">Data Leakage Prevented</option>
          </select>
          <select
            value={riskLevelFilter}
            onChange={(e) => { setRiskLevelFilter(e.target.value); setEventsPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Risk Levels</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>
      </div>

      <div className="text-sm text-gray-500">
        Showing {events.length} of {eventsTotal} events
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <ShieldCheck className="w-12 h-12 text-green-300 mx-auto mb-3" />
                    <div className="text-gray-500">No security events found for the selected period</div>
                    <div className="text-sm text-green-600 mt-1">All systems operating normally</div>
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(event.event_timestamp)}</td>
                    <td className="px-4 py-3">{getEventTypeBadge(event.event_type)}</td>
                    <td className="px-4 py-3">{getRiskBadge(event.risk_level)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{event.user_name || `User #${event.user_id || 'N/A'}`}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{event.tenant_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{event.ip_address || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{event.resource_name || event.table_name || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {eventsTotalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setEventsPage(Math.max(1, eventsPage - 1))}
            disabled={eventsPage === 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {eventsPage} of {eventsTotalPages}
          </span>
          <button
            onClick={() => setEventsPage(Math.min(eventsTotalPages, eventsPage + 1))}
            disabled={eventsPage === eventsTotalPages}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  const renderStoredReports = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {storedReports.length} stored report{storedReports.length !== 1 ? 's' : ''}
        </div>
        <button
          onClick={generateReportNow}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50"
        >
          <FileText className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
          <span>Generate Report Now</span>
        </button>
      </div>

      {storedReports.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <div className="text-gray-500 mb-2">No stored compliance reports yet</div>
          <div className="text-sm text-gray-400">
            Reports are auto-generated on the 1st of each month, or you can generate one manually.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {storedReports.map((report) => (
            <div key={report.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-lg ${
                    report.compliance_status === 'COMPLIANT' ? 'bg-green-100' :
                    report.compliance_status === 'NEEDS_REVIEW' ? 'bg-yellow-100' : 'bg-red-100'
                  }`}>
                    {getStatusIcon(report.compliance_status)}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{report.period}</div>
                    <div className="text-sm text-gray-500">Generated: {formatDate(report.generated_at)}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(report.compliance_status)}`}>
                      {report.compliance_status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-500">
                    <span>{report.total_security_events} events</span>
                    <span>{report.critical_events} critical</span>
                  </div>
                  {expandedReport === report.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              {expandedReport === report.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="text-xl font-bold text-red-600">{report.cross_tenant_access_attempts}</div>
                      <div className="text-xs text-gray-500">Cross-Tenant Blocks</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="text-xl font-bold text-orange-600">{report.rate_limit_violations}</div>
                      <div className="text-xs text-gray-500">Rate Limit Violations</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="text-xl font-bold text-purple-600">{report.data_leakage_preventions}</div>
                      <div className="text-xs text-gray-500">Leakage Prevented</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="text-xl font-bold text-gray-800">{report.total_security_events}</div>
                      <div className="text-xs text-gray-500">Total Events</div>
                    </div>
                  </div>
                  {report.report_data && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">
                        View Full Report Data
                      </summary>
                      <pre className="mt-2 bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto text-xs text-gray-600 max-h-96">
                        {JSON.stringify(report.report_data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <ShieldCheck className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Tenant Isolation Compliance</h2>
              <p className="text-sm text-gray-500">SOC 2 / ISO 27001 compliant security monitoring & reporting</p>
            </div>
          </div>
        </div>

        <div className="flex space-x-2 border-b border-gray-200 mb-6 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('live-report')}
            className={`flex items-center space-x-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'live-report'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Activity className="w-4 h-4" />
            <span>Live Report</span>
          </button>
          <button
            onClick={() => setActiveTab('security-events')}
            className={`flex items-center space-x-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'security-events'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Eye className="w-4 h-4" />
            <span>Security Events</span>
          </button>
          <button
            onClick={() => setActiveTab('stored-reports')}
            className={`flex items-center space-x-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'stored-reports'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Monthly Reports</span>
          </button>
        </div>

        {loading && !report && events.length === 0 && storedReports.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-500">Loading...</span>
          </div>
        ) : (
          <>
            {activeTab === 'live-report' && renderLiveReport()}
            {activeTab === 'security-events' && renderSecurityEvents()}
            {activeTab === 'stored-reports' && renderStoredReports()}
          </>
        )}
      </div>
    </div>
  );
};

export default TenantIsolationCompliance;
